import { Controller } from "@hotwired/stimulus"

// In-lesson Rating Table runtime. Each row is its own radio group, so the browser
// already enforces one rating per item; this controller only carries the rating to the
// server and says whether it stuck.
//
// Nothing aggregate comes back. A self-assessment is stored, not compared, so there is
// no tally to reveal and no markup to build — the grid the learner sees is entirely
// server-rendered, including the ratings they gave on an earlier visit.
//
// In the course PREVIEW the grid is answerable but nothing is recorded: an author has
// to be able to see it behave before they publish. The server never hears about it —
// the view omits the submit URL entirely in preview, so the request is unconstructible
// rather than merely skipped.
//
// The reader sets data-turbo="false", so the rating goes out as a fetch with the CSRF
// meta token rather than a form post: a redirect would lose the learner's place
// mid-lesson.
export default class extends Controller {
  static targets = ["item", "status"]
  static values = {
    submitUrl: String,
    preview: Boolean
  }

  async rate(event) {
    const item = event.currentTarget.closest("[data-rating-item-key]")
    const ratingItemKey = item?.dataset.ratingItemKey
    const scalePointKey = event.currentTarget.value
    if (!ratingItemKey || !scalePointKey) return

    if (this.previewValue || !this.hasSubmitUrlValue) return

    const saved = await this.persist(ratingItemKey, scalePointKey)
    // Only a failure is worth saying out loud — a rating that saved is already visible
    // as the row the learner just picked.
    this.announce(saved ? "" : "We couldn't save that rating. Check your connection and try again.")
  }

  async persist(ratingItemKey, scalePointKey) {
    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        credentials: "same-origin",
        body: JSON.stringify({ rating_item_key: ratingItemKey, scale_point_key: scalePointKey })
      })
      const data = await response.json().catch(() => ({}))

      return response.ok && data.success
    } catch (error) {
      return false
    }
  }

  announce(message) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.hidden = message === ""
  }
}
